// src/app/jobs/browse-jobs-index/browse-jobs-index.component.ts
import { Component, computed, inject, signal, OnInit, DestroyRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { SeoService } from '@/app/core/seo/seo.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DecimalPipe } from '@angular/common';
import { BrowseJobsService, TaxonomyItem } from '../browse-jobs.service';
import { JobCountsPublicDto } from '@/app/models/public';

interface DimensionSection {
  slug: string;
  label: string;
  items: TaxonomyItem[];
}

interface QuickLink {
    label: string;
    url?: string;
    anchor?: string;
    desc: string;
  }
  
  const QUICK_LINKS: QuickLink[] = [
    { label: 'Browse by State',     anchor: 'state',        desc: 'Find truck driving jobs across the country' },
    { label: 'Company Driver Jobs', url: '/browse-jobs/driver-type/company-driver', desc: 'Browse jobs for company drivers' },
    { label: 'CDL Class A Jobs',    url: '/browse-jobs/cdl-class/class-a',          desc: 'See open Class A driving jobs' },
    { label: 'Freight Type Jobs',   anchor: 'freight-type', desc: 'Browse by trailer and freight type' },
  ];
  

@Component({
  selector: 'app-browse-jobs-index',
  templateUrl: './browse-jobs-index.component.html',
  imports: [
    RouterLink,
    MatButtonModule,
    MatProgressSpinnerModule,
    DecimalPipe,
  ],
})
export class BrowseJobsIndexComponent implements OnInit {
  private http        = inject(HttpClient);
  private seo         = inject(SeoService);
  private browseService = inject(BrowseJobsService);
  private destroyRef  = inject(DestroyRef);

  loading  = signal(true);
  sections = signal<DimensionSection[]>([]);

  readonly quickLinks = QUICK_LINKS;
  readonly dimensionOrder = Object.keys(this.browseService.taxonomyMaps);

  ngOnInit(): void {
    this.seo.setPage({
      title: 'Browse Truck Driving Jobs',
      description: 'Browse truck driving jobs by state, driver type, CDL class, freight type, endorsement, '
        + 'and experience level on Truck Drivers USA.',
      path: '/browse-jobs',
    });

    this.http.get<JobCountsPublicDto>('/api/public/jobs/counts').pipe(
      catchError(() => of(null)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(counts => {
      if (!counts) { this.loading.set(false); return; }

      const lookup = this.browseService.buildCountLookup({
        byState:          counts.byState,
        byDriverType:     counts.byDriverType,
        byCdlClass:       counts.byCdlClass,
        byFreightType:    counts.byFreightType,
        byEndorsement:    counts.byEndorsement,
        byExperienceType: counts.byExperienceType,
      });

      const sections: DimensionSection[] = [];

      for (const slug of this.dimensionOrder) {
        const sortByCount = slug !== 'state';
        const items = this.browseService.getItemsForDimension(slug, lookup, [], sortByCount);
        if (!items.length) continue;

        sections.push({
          slug,
          label: this.browseService.sectionLabels[slug] ?? slug,
          items,
        });
      }

      this.sections.set(sections);
      this.loading.set(false);
    });
  }

  scrollToSection(id: string): void {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }  
}
